import type { PlanTier } from '../context/ConfigurationContext';

export interface PlanFeature {
  id: string;
  name: string;
  displayName: string; // Emotional name for display
  description: string;
  fearAddressed?: string; // What caregiver fear this addresses
}

export interface Plan {
  id: PlanTier;
  name: string;
  tagline: string;
  subtext: string;
  price: number;
  priceDisplay: string;
  recommended?: boolean;
  features: {
    communication: string[];
    safety: string[];
    health: string[];
  };
  featureCount: {
    communication: number;
    safety: number;
    health: number;
    total: number;
  };
}

// All features organized by category
export const ALL_FEATURES: Record<string, PlanFeature> = {
  // COMMUNICATION (8 features - all tiers)
  simplifiedCalling: {
    id: 'simplifiedCalling',
    name: 'Simplified Calling',
    displayName: 'Simplified Calling',
    description: 'Large buttons, one-tap to call',
  },
  photoMessaging: {
    id: 'photoMessaging',
    name: 'Photo Messaging',
    displayName: 'Photo Messaging',
    description: 'Send photos to family easily',
  },
  voiceAssistant: {
    id: 'voiceAssistant',
    name: 'Voice Assistant',
    displayName: 'Voice Assistant',
    description: '"Call Marie" voice commands',
  },
  contactFavorites: {
    id: 'contactFavorites',
    name: 'Contact Favorites',
    displayName: 'Contact Favorites',
    description: 'Quick access to important people',
  },
  oneTapEmergency: {
    id: 'oneTapEmergency',
    name: 'One-tap Emergency (911)',
    displayName: 'One-tap Emergency',
    description: 'Instant emergency access',
  },
  voiceMessages: {
    id: 'voiceMessages',
    name: 'Voice Messages',
    displayName: 'Voice Messages',
    description: 'Record and send audio',
  },
  largeButtonInterface: {
    id: 'largeButtonInterface',
    name: 'Large Button Interface',
    displayName: 'Large Button Interface',
    description: 'Easy to see and tap',
  },
  autoAnswer: {
    id: 'autoAnswer',
    name: 'Auto-Answer Option',
    displayName: 'Auto-Answer Option',
    description: 'Automatically answer trusted callers',
  },

  // SAFETY - Essential (3 features)
  emergencySOS: {
    id: 'emergencySOS',
    name: 'Emergency SOS Button',
    displayName: 'Emergency SOS Button',
    description: 'One tap sends alert',
  },
  emergencyContactAlert: {
    id: 'emergencyContactAlert',
    name: 'Emergency Contact Alert',
    displayName: 'Emergency Contact Alert',
    description: 'Notifies family of SOS',
  },
  lowBatteryAlerts: {
    id: 'lowBatteryAlerts',
    name: 'Low Battery Alerts',
    displayName: 'Low Battery Alerts',
    description: 'Caregiver notified when low',
  },

  // SAFETY - Plus adds
  // REMOVED: the `geoFencing` entry — "Geo-fencing" / "Know if mom wanders" /
  // "Get alerted if they leave a safe area" / "Will I know if she leaves?".
  // Geofencing is CUT from v1 (Feature 03 spec). The entry was referenced by
  // nothing, so it rendered nowhere, but it sat in a constants module that the
  // locked-facts build gate cannot see (it scans src/**/*.tsx, not .ts) and it
  // was one import away from being shown to a caregiver as a plan feature.
  gpsLocation: {
    id: 'gpsLocation',
    name: 'GPS Location Sharing',
    displayName: 'See where she is',
    description: 'Real-time location tracking',
    fearAddressed: 'Where is she right now?',
  },
  phoneDropAlert: {
    id: 'phoneDropAlert',
    name: 'Phone Drop Alert',
    displayName: 'Know if something happened',
    description: 'Detects sudden phone movement',
    fearAddressed: 'Did she fall?',
  },
  voiceDistressDetection: {
    id: 'voiceDistressDetection',
    name: 'Voice Distress Detection',
    displayName: 'Know if she needs help',
    description: 'AI detects distress in voice',
    fearAddressed: 'Is she in trouble?',
  },
  inactivityAlerts: {
    id: 'inactivityAlerts',
    name: 'Inactivity Alerts',
    displayName: 'Know if she\'s okay',
    description: 'Daily activity monitoring',
    fearAddressed: 'Is she moving today?',
  },
  caregiverDashboard: {
    id: 'caregiverDashboard',
    name: 'Caregiver Dashboard',
    displayName: 'See her activity anytime',
    description: 'View all activity in one place',
    fearAddressed: 'How is she doing?',
  },
  scamCallBlocking: {
    id: 'scamCallBlocking',
    name: 'Scam Call Blocking',
    displayName: 'Scam protection',
    description: 'Blocks known scam numbers',
    fearAddressed: 'Is someone tricking her?',
  },

  // SAFETY - Complete adds (2 features)
  locationHistory: {
    id: 'locationHistory',
    name: 'Location History',
    displayName: 'Location History',
    description: 'See where she\'s been',
  },
  multipleCaregiverAccess: {
    id: 'multipleCaregiverAccess',
    name: 'Multiple Caregiver Access',
    displayName: 'Multiple Caregiver Access',
    description: 'Share with siblings',
  },

  // HEALTH - Essential (1 feature)
  appointmentReminders: {
    id: 'appointmentReminders',
    name: 'Appointment Reminders',
    displayName: 'Appointment Reminders',
    description: 'Basic calendar alerts',
  },

  // HEALTH - Complete adds (11 features)
  medicationReminders: {
    id: 'medicationReminders',
    name: 'Medication Reminders',
    displayName: 'Medication Reminders',
    description: 'Voice + visual reminders',
  },
  medicationTracking: {
    id: 'medicationTracking',
    name: 'Medication Tracking',
    displayName: 'Medication Tracking',
    description: 'Log when taken',
  },
  videoCalling: {
    id: 'videoCalling',
    name: 'Video Calling',
    displayName: 'Video Calling',
    description: 'Face-to-face connection',
  },
  sleepMonitoring: {
    id: 'sleepMonitoring',
    name: 'Sleep Monitoring',
    displayName: 'Sleep Monitoring',
    description: 'Phone placement analysis',
  },
  dailyWellnessCheckIn: {
    id: 'dailyWellnessCheckIn',
    name: 'Daily Wellness Check-in',
    displayName: 'Daily Wellness Check-in',
    description: '"How are you feeling?"',
  },
  healthReportFamily: {
    id: 'healthReportFamily',
    name: 'Health Report for Family',
    displayName: 'Health Report for Family',
    description: 'Weekly summary',
  },
  stepCounter: {
    id: 'stepCounter',
    name: 'Step Counter',
    displayName: 'Step Counter',
    description: 'Activity tracking',
  },
  heartRate: {
    id: 'heartRate',
    name: 'Heart Rate (camera)',
    displayName: 'Heart Rate Monitor',
    description: 'Pulse measurement',
  },
  oximeter: {
    id: 'oximeter',
    name: 'Oximeter (camera)',
    displayName: 'Blood Oxygen Monitor',
    description: 'Blood oxygen estimate',
  },
  prioritySupport: {
    id: 'prioritySupport',
    name: 'Priority Support 24/7',
    displayName: 'Priority Support 24/7',
    description: 'Human help anytime',
  },
  caregiverNotes: {
    id: 'caregiverNotes',
    name: 'Caregiver Notes',
    displayName: 'Caregiver Notes',
    description: 'Share notes with family',
  },
};

// Feature IDs by category
const COMMUNICATION_FEATURES = [
  'simplifiedCalling', 'photoMessaging', 'voiceAssistant', 'contactFavorites',
  'oneTapEmergency', 'voiceMessages', 'largeButtonInterface', 'autoAnswer',
];

const SAFETY_ESSENTIAL = ['emergencySOS', 'emergencyContactAlert', 'lowBatteryAlerts'];
// 'geoFencing' removed 2026-08-16 with the PLAN_FEATURES entry it pointed at —
// geofencing is CUT from v1. Leaving the id here would have left a dangling
// lookup into a feature that no longer exists.
const SAFETY_PLUS = [...SAFETY_ESSENTIAL, 'gpsLocation', 'phoneDropAlert', 'voiceDistressDetection', 'inactivityAlerts', 'caregiverDashboard', 'scamCallBlocking'];
const HEALTH_ESSENTIAL = ['appointmentReminders'];

// Single plan
export const PLANS: Plan[] = [
  {
    id: 'plus',
    name: 'Protection',
    tagline: 'Know if something\'s wrong',
    subtext: 'Safety features for caregivers',
    price: 10.99,
    priceDisplay: '$10.99',
    recommended: true,
    features: {
      communication: COMMUNICATION_FEATURES,
      safety: SAFETY_PLUS,
      health: HEALTH_ESSENTIAL,
    },
    featureCount: {
      communication: 8,
      safety: 10,
      health: 1,
      total: 19,
    },
  },
];

